import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import NumberFormat from 'react-number-format';
import './styles.css';
import REVIEW_TYPES from '../../../../constants/review-types';
import LinkBuilder from '../../../../helpers/link-builder';
import StringHelpers from '../../../../helpers/string-helpers';
import {Role} from '../../../../helpers/user';
import CommentsBlock from '../../../../scenes/CommentsBlock';
import CommentIconButton from '../../../CommentIconButton';
import RatingStarsWidget from '../../../RatingStarsWidget';
import RatingStarsWidgetEditable from '../../../RatingStarsWidgetEditable';

const FirmReviewItem = props => {
  const {
    user,
    user: { isLoggedIn },
    review,
    actions: {
      handleClickReviewLink,
      handleBeforeReviewRatingUpdate,
      handleReviewRatingUpdate,
      handleReviewShowPreviousComments,
      handleReviewAddComment,
      handleReviewRemoveComment,
    },
    isFetchingComments,
  } = props;

  const {
    ReviewID,
    ReviewType,
    ReviewType: baseType,
    TitleName,
    DateAdd,
    Text,
    Rating,
    UrlAlias,
    ResponseCount,
    PublicName,
    Position,
    HasCandidateRating,
    AllowView,
    Comments,
    CommentsCount,
  } = review;

  const [isCommentsBlockOpen, setCommentsBlockOpen] = useState(false);

  const commentBlock = useRef(null);

  useEffect(() => {
    if (isCommentsBlockOpen) {
      commentBlock.current.focus();
    }
  }, [isCommentsBlockOpen]);

  const reviewType =
    baseType === REVIEW_TYPES.OVERVIEW ? REVIEW_TYPES.COMPANY : baseType;

  const responseText = ResponseCount ? (
    <>
      <NumberFormat
        thousandSeparator
        value={ResponseCount}
        displayType={'text'}
      />{' '}
      {ResponseCount === 1 ? 'response' : 'responses'}
    </>
  ) : (
    'No responses'
  );

  let link = null;
  if (AllowView === 'No' && isLoggedIn) {
    link = '#';
  } else if (ReviewType === REVIEW_TYPES.EMPLOYEE) {
    link = LinkBuilder.getEmployeeLink(UrlAlias, PublicName);
  } else if (ReviewType === REVIEW_TYPES.OVERVIEW) {
    link = LinkBuilder.getOverviewLink(UrlAlias, PublicName);
  } else if (ReviewType === REVIEW_TYPES.INTERVIEW) {
    link = LinkBuilder.getInterviewLink(UrlAlias, PublicName);
  }

  let reviewTypeName;

  if (ReviewType === REVIEW_TYPES.EMPLOYEE) {
    reviewTypeName = 'Employee Review';
  } else if (ReviewType === REVIEW_TYPES.OVERVIEW) {
    reviewTypeName = 'Company Review';
  } else if (ReviewType === REVIEW_TYPES.INTERVIEW) {
    reviewTypeName = 'Interview Review';
  }

  const showCommentsBlock = () => {
    if (Role.isAnonymous(user)) {
      window.location = '/signup';
    } else {
      setCommentsBlockOpen(isCommentsBlockOpen => !isCommentsBlockOpen);
    }
  };

  const handleLinkClick = e => handleClickReviewLink(e, review);

  const handleRatingUpdate = value => {
    handleReviewRatingUpdate(review, value);
  };

  const handleBeforeRatingUpdate = () => {
    return handleBeforeReviewRatingUpdate(review);
  };

  const handleAddComment = comment => {
    !isFetchingComments &&
      handleReviewAddComment({
        type: ReviewType,
        reviewId: ReviewID,
        comment,
      });
  };

  const handleRemoveComment = id => {
    !isFetchingComments &&
      handleReviewRemoveComment({
        type: ReviewType,
        reviewId: ReviewID,
        commentId: id,
      });
  };

  const handleClickLoadCommentButton = () => {
    !isFetchingComments &&
      handleReviewShowPreviousComments({
        type: ReviewType,
        reviewId: ReviewID,
        commentId: Comments.length ? Comments[0].ID : null,
      });
  };

  return (
    <article className="firm-review-list__item">
      <div className="firm-review-list__item-row">
        <h4 className="firm-review-list__item-title">
          <NavLink
            to={link}
            className={'firm-review-list__item-title-link'}
            onClick={handleLinkClick}
          >
            {Position && reviewType === 'employee' ? Position : TitleName}
          </NavLink>
        </h4>

        <span className="firm-review-list__item-date">{DateAdd}</span>
      </div>

      <div className="firm-review-list__item-row">
        <div className="firm-review-list__item-rating">
          {HasCandidateRating || !isLoggedIn ? (
            <RatingStarsWidget value={Rating} size={15} showNumber={false} />
          ) : (
            <RatingStarsWidgetEditable
              value={0}
              size={15}
              showNumber={false}
              onChangeHandler={handleRatingUpdate}
              onBeforeChangeHandler={handleBeforeRatingUpdate}
            />
          )}
          <span className="firm-review-list__item-rating-comment">
            {HasCandidateRating || !isLoggedIn
              ? responseText
              : 'Rate this review'}
          </span>
        </div>
        <span className="firm-review-list__item-type">{reviewTypeName}</span>
      </div>

      <div className="firm-review-list__item-text">
        {StringHelpers.trimString(Text, 16, '... ')}
        <NavLink
          className="read-more-button"
          to={link}
          onClick={handleLinkClick}
        >
          Read more
        </NavLink>
      </div>
      <div className="firm-review-list__item-footer">
        <div className="firm-review-list__item-icon">
          <CommentIconButton
            count={CommentsCount}
            onClick={showCommentsBlock}
          />
        </div>
      </div>
      {isCommentsBlockOpen && (
        <CommentsBlock
          ref={commentBlock}
          user={user}
          comments={Comments}
          count={CommentsCount}
          isFetching={isFetchingComments}
          onSendComment={handleAddComment}
          onRemove={handleRemoveComment}
          onClickLoadButton={handleClickLoadCommentButton}
        />
      )}
    </article>
  );
};

export default FirmReviewItem;
